if (!customElements.get('shopify-form-wrapper')) {
  class ShopifyFormWrapper extends HTMLElement {
    constructor() {
      super();

      this.observer = null;
      this.shadowObserver = null;
      this.uploadPreviewUrl = null;
      this.uploadPreviewFileKey = null;
      this.isMessageCharacterCountEnabled = false;
      this.messageMaxChars = null;

      this.onMutations = this.onMutations.bind(this);
      this.onShadowMutations = this.onShadowMutations.bind(this);
    }

    connectedCallback() {
      try {
        this.isMessageCharacterCountEnabled = this.dataset.enableMessageCharacterCount === 'true';
        this.messageMaxChars = this.parseMessageMaxChars();
        this.observer = new MutationObserver(this.onMutations);

        this.observer.observe(this, {
          childList: true,
          subtree: true
        });

        this.initShopifyForm();
      } catch (_error) {
        this.cleanup();
      }
    }

    disconnectedCallback() {
      this.cleanup();
    }

    cleanup() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      if (this.shadowObserver) {
        this.shadowObserver.disconnect();
        this.shadowObserver = null;
      }

      this.revokeUploadPreviewUrl();
    }

    onMutations(mutations) {
      try {
        const hasInjectedNodes = mutations.some((mutation) => mutation.addedNodes.length > 0);
        if (!hasInjectedNodes) return;

        this.initShopifyForm();
      } catch (_error) {
        // Prevent wrapper errors from breaking Shopify Forms or the page.
      }
    }

    onShadowMutations(mutations) {
      try {
        const hasInjectedNodes = mutations.some((mutation) => mutation.addedNodes.length > 0);
        if (!hasInjectedNodes) return;

        this.updateFileInputButton();
        this.updateMessageCharacterCount();
      } catch (_error) {
        // Prevent wrapper errors from breaking Shopify Forms or the page.
      }
    }

    initShopifyForm() {
      const embed = this.querySelector('shopify-forms-embed#app-embed');

      if (!embed?.shadowRoot) return;

      this.injectShadowStyles(embed.shadowRoot);
      this.observeShadowRoot(embed.shadowRoot);
      this.updateFileInputButton();
      this.updateMessageCharacterCount();
    }

    observeShadowRoot(shadowRoot) {
      if (this.shadowObserver) return;

      this.shadowObserver = new MutationObserver(this.onShadowMutations);

      this.shadowObserver.observe(shadowRoot, {
        childList: true,
        subtree: true
      });
    }

    injectShadowStyles(shadowRoot) {
      if (shadowRoot.querySelector('[data-shopify-form-wrapper-styles]')) return;

      const style = document.createElement('style');

      style.setAttribute('data-shopify-form-wrapper-styles', '');

      style.textContent = `
        :host {
          --form-field-border: 0 0 0 1px var(--shopify-form-input-outline-color, #171717);
        }

        .shopify-form-content {
          display: flex;
          flex-direction: column;
          gap: 4px;
          padding: 45px 30px;
          text-align: center;
        }

        .shopify-form-content__icon {
          display: flex;
          justify-content: center;
        }

        .shopify-form-content__icon svg {
          transition: fill 0.2s ease, stroke 0.2s ease;
        }

        .shopify-form-upload-button {
          border: 2px dashed var(--shopify-form-font-color, var(--color-black-1)) !important;
          border-radius: 10px;
          background: transparent;
          min-height: 240px;
        }

        .shopify-form-upload-button.shopify-form-upload-has-file {
          border: none !important;
          min-height: unset;
        }

        .shopify-form-upload-button.shopify-form-upload-has-file .shopify-form-content {
          display: none;
        }

        .shopify-form-upload-button [class*="formFileInputButton"] {
          display: none !important;
        }

        .shopify-form-upload-button [class*="formFileInputFileName"] {
          display: flex !important;
          align-items: center;
          gap: 12px;
          width: 100%;
          padding: 12px 14px;
          box-sizing: border-box;
          background: var(--shopify-form-upload-file-background-color, #FAFAFA);
          border-radius: 8px;
        }

        .shopify-form-upload-button [class*="formFileInputFileName"] span {
          flex: 1;
          min-width: 0;
          text-align: left;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .shopify-form-upload-button [class*="formFileInputFileName"] span.shopify-form-upload-file-size {
          flex: 0 0 auto;
          min-width: auto;
          text-align: right;
          white-space: nowrap;
          overflow: visible;
          text-overflow: clip;
          color: inherit;
          font-size: 13px;
        }

        .shopify-form-upload-button [class*="formFileInputCancelButton"] {
          flex-shrink: 0;
          color: #98A0B4;
        }

        .shopify-form-upload-button [class*="formFileInputCancelButton"] svg {
          display: block;
          width: 16px;
          height: 16px;
        }

        .shopify-form-upload-button [class*="formFileInputCancelButton"] svg path {
          fill: currentColor;
        }

        .shopify-form-upload-preview-thumbnail {
          width: 45px;
          height: 45px;
          object-fit: cover;
          border-radius: 6px;
          flex-shrink: 0;
        }

        .shopify-form-upload-preview-thumbnail-wrap {
          width: 45px;
          height: 45px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          overflow: hidden;
        }

        button:hover .shopify-form-content__icon svg [fill]:not([fill="none"]) {
          fill: var(--shopify-form-icon-hover-color);
        }

        button:hover .shopify-form-content__icon svg [stroke]:not([stroke="none"]) {
          stroke: var(--shopify-form-icon-hover-color);
        }

        .shopify-form-content p,
        .shopify-form-content span,
        .shopify-form-content a,
        .shopify-form-content strong {
          font-family: var(--font-body-family, inherit);
          font-weight: 500;
          font-style: normal;
          font-size: 14px;
          line-height: 130%;
          letter-spacing: 0;
          text-align: center;
          color: var(--shopify-form-font-color);
          margin: 0;
        }

        .shopify-form-content strong {
          color: var(--shopify-form-highlight-color);
        }

        .shopify-form-content__error {
          display: none;
          margin-top: 8px;
          font-family: var(--font-body-family, inherit);
          font-size: 13px;
          line-height: 130%;
          color: var(--color-red);
          text-align: center;
        }

        .shopify-form-content__error.is-visible {
          display: block;
        }

        .shopify-form-message-char-count {
          margin-top: 6px;
          font-family: var(--font-body-family, inherit);
          font-size: 12px;
          line-height: 130%;
          color: var(--shopify-form-font-color, #4B4B4B);
          text-align: right;
        }
      `;

      shadowRoot.appendChild(style);
    }

    updateFileInputButton() {
      const buttonCopy = this.dataset.uploadButtonCopy;
      const fontColor = this.dataset.uploadButtonFontColor || '#4B4B4B';
      const highlightColor = this.dataset.uploadButtonHighlightColor || '#0832F1';
      const iconHoverColor = this.dataset.uploadIconHoverColor || '#183FF2';
      const inputOutlineColor = this.dataset.uploadInputOutlineColor || '#171717';
      const uploadFileBackgroundColor = this.dataset.uploadFileBackgroundColor || '#FAFAFA';
      const errorText = this.dataset.uploadErrorText;

      if (!buttonCopy) return;

      const embed = this.querySelector('shopify-forms-embed#app-embed');
      const root = embed?.shadowRoot || this;
      if (embed) {
        embed.style.setProperty('--shopify-form-input-outline-color', inputOutlineColor);
        embed.style.setProperty('--shopify-form-font-color', fontColor);
        embed.style.setProperty('--shopify-form-upload-file-background-color', uploadFileBackgroundColor);
      }
      const fileInput = root.querySelector('input[type="file"]');

      if (!fileInput) return;

      const button = fileInput.closest('button');

      if (!button) return;

      this.bindFileInputChange(fileInput, button);

      button.style.setProperty('--shopify-form-font-color', fontColor);
      button.style.setProperty('--shopify-form-highlight-color', highlightColor);
      button.style.setProperty('--shopify-form-icon-hover-color', iconHoverColor);

      if (button.querySelector('.shopify-form-content')) {
        this.syncUploadContentVisibility(button);
        this.syncUploadFileSize(button, fileInput);
        this.syncUploadPreviewImage(button, fileInput);
        this.syncUploadErrorState(root, button, errorText);
        return;
      }

      button.innerHTML = '';
      button.classList.add('shopify-form-upload-button');

      const contentColumn = button.ownerDocument.createElement('div');
      const textColumn = button.ownerDocument.createElement('div');
      const icon = this.querySelector('.js-shopify-form-wrapper-icon');

      contentColumn.className = 'shopify-form-content';
      textColumn.className = 'shopify-form-content__text';

      if (icon?.textContent?.trim()) {
        const iconWrapper = button.ownerDocument.createElement('div');

        iconWrapper.className = 'shopify-form-content__icon';
        iconWrapper.innerHTML = icon.textContent.trim();

        contentColumn.appendChild(iconWrapper);
      }

      textColumn.innerHTML = buttonCopy;
      contentColumn.appendChild(textColumn);

      if (errorText) {
        const errorMessage = button.ownerDocument.createElement('p');

        errorMessage.className = 'shopify-form-content__error';
        errorMessage.textContent = errorText;

        contentColumn.appendChild(errorMessage);
      }

      button.appendChild(contentColumn);

      this.syncUploadContentVisibility(button);
      this.syncUploadFileSize(button, fileInput);
      this.syncUploadPreviewImage(button, fileInput);
      this.syncUploadErrorState(root, button, errorText);
    }

    bindFileInputChange(fileInput, button) {
      if (fileInput.dataset.shopifyFormUploadBound === 'true') return;

      fileInput.dataset.shopifyFormUploadBound = 'true';

      fileInput.addEventListener('change', () => {
        window.setTimeout(() => {
          try {
            const embed = this.querySelector('shopify-forms-embed#app-embed');
            const root = embed?.shadowRoot || this;
            const errorText = this.dataset.uploadErrorText;

            this.syncUploadContentVisibility(button);
            this.syncUploadFileSize(button, fileInput);
            this.syncUploadPreviewImage(button, fileInput);
            this.syncUploadErrorState(root, button, errorText, fileInput);
          } catch (_error) {
            // Prevent file preview errors from breaking Shopify Forms.
          }
        }, 0);
      });
    }

    syncUploadFileSize(button, fileInput) {
      const fileRow = this.getShopifyFileRow(button);

      if (!fileRow) return;

      const file = fileInput.files?.[0];
      const existingSize = fileRow.querySelector('.shopify-form-upload-file-size');

      if (!file) {
        if (existingSize) existingSize.remove();
        return;
      }

      const sizeLabel = this.formatFileSize(file.size);

      if (existingSize) {
        existingSize.textContent = sizeLabel;
        return;
      }

      const sizeEl = button.ownerDocument.createElement('span');

      sizeEl.className = 'shopify-form-upload-file-size';
      sizeEl.textContent = sizeLabel;

      const clearButton = fileRow.querySelector('[class*="formFileInputCancelButton"]');

      if (clearButton) {
        fileRow.insertBefore(sizeEl, clearButton);
        return;
      }

      fileRow.appendChild(sizeEl);
    }

    syncUploadContentVisibility(button) {
      const clearButton = button.querySelector('[id$="-clear"]');
      const fileNameEl = button.querySelector('[class*="formFileInputFileName"] span');
      const hasShopifyFile = Boolean(clearButton && fileNameEl?.textContent?.trim());

      button.classList.toggle('shopify-form-upload-has-file', hasShopifyFile);

      if (!hasShopifyFile) {
        this.removeUploadPreviewImage(button);
        this.revokeUploadPreviewUrl();
      }
    }

    syncUploadPreviewImage(button, fileInput) {
      const file = fileInput.files?.[0];

      if (!file || !file.type.startsWith('image/')) {
        this.removeUploadPreviewImage(button);
        return;
      }

      const fileRow = this.getShopifyFileRow(button);

      if (!fileRow) {
        return;
      }

      const previewFileKey = `${file.name}:${file.size}:${file.lastModified}`;
      const existingThumbnail = fileRow.querySelector('.shopify-form-upload-preview-thumbnail');

      if (existingThumbnail && this.uploadPreviewFileKey === previewFileKey) {
        this.syncUploadClearIcon(button);
        return;
      }

      this.syncUploadClearIcon(button);
      this.removeUploadPreviewImage(button);
      this.revokeUploadPreviewUrl();

      this.uploadPreviewUrl = URL.createObjectURL(file);
      this.uploadPreviewFileKey = previewFileKey;

      const img = button.ownerDocument.createElement('img');
      const imgWrap = button.ownerDocument.createElement('div');

      img.src = this.uploadPreviewUrl;
      img.alt = '';
      img.className = 'shopify-form-upload-preview-thumbnail';
      imgWrap.className = 'shopify-form-upload-preview-thumbnail-wrap';
      imgWrap.appendChild(img);

      fileRow.prepend(imgWrap);
    }

    syncUploadClearIcon(button) {
      const icon = this.querySelector('.js-shopify-form-wrapper-icon-close');
      const clearButton = button.querySelector('[id$="-clear"]');

      if (!icon?.textContent?.trim() || !clearButton) return;

      if (clearButton.dataset.shopifyFormIconReplaced !== 'true') {
        const existingSvg = clearButton.querySelector('svg');

        if (existingSvg) {
          existingSvg.outerHTML = icon.textContent.trim();
          clearButton.dataset.shopifyFormIconReplaced = 'true';
        }
      }

      if (clearButton.dataset.shopifyFormClearBound === 'true') return;

      clearButton.dataset.shopifyFormClearBound = 'true';

      clearButton.addEventListener('click', () => {
        window.setTimeout(() => {
          try {
            const embed = this.querySelector('shopify-forms-embed#app-embed');
            const root = embed?.shadowRoot || this;
            const fileInput = root.querySelector('input[type="file"]');
            const errorText = this.dataset.uploadErrorText;

            this.syncUploadContentVisibility(button);
            this.removeUploadPreviewImage(button);
            this.syncUploadErrorState(root, button, errorText, fileInput);
          } catch (_error) {
            // Prevent clear errors from breaking Shopify Forms.
          }
        }, 0);
      });
    }

    removeUploadPreviewImage(button) {
      const existingThumbnailWrap = button.querySelector('.shopify-form-upload-preview-thumbnail-wrap');

      if (existingThumbnailWrap) {
        existingThumbnailWrap.remove();
      }

      this.uploadPreviewFileKey = null;
    }

    revokeUploadPreviewUrl() {
      if (!this.uploadPreviewUrl) return;

      URL.revokeObjectURL(this.uploadPreviewUrl);
      this.uploadPreviewUrl = null;
      this.uploadPreviewFileKey = null;
    }

    getShopifyFileRow(button) {
      const clearButton = button.querySelector('[id$="-clear"]');

      if (clearButton) {
        return clearButton.closest('div');
      }

      const fileNameSpan = button.querySelector('[class*="formFileInputFileName"] span');

      if (fileNameSpan) {
        return fileNameSpan.closest('div');
      }

      return null;
    }

    formatFileSize(bytes) {
      if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
      if (bytes < 1024) return `${bytes} B`;

      const units = ['KB', 'MB', 'GB'];
      let size = bytes / 1024;
      let unitIndex = 0;

      while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex += 1;
      }

      const decimals = size >= 10 ? 0 : 1;

      return `${size.toFixed(decimals)} ${units[unitIndex]}`;
    }

    syncUploadErrorState(root, button, errorText, fileInput = null) {
      if (!errorText) return;

      const errorMessage = button.querySelector('.shopify-form-content__error');

      if (!errorMessage) return;

      const hasInputError = Boolean(
        (fileInput &&
          (fileInput.matches('[aria-invalid="true"], [data-invalid], .invalid') ||
            fileInput.closest('[aria-invalid="true"], [data-invalid], .field--error, .errors'))) ||
          root.querySelector(
            '[aria-invalid="true"][type="file"], [data-invalid] input[type="file"], .field--error input[type="file"], .errors input[type="file"]'
          )
      );

      errorMessage.classList.toggle('is-visible', hasInputError);
    }

    updateMessageCharacterCount() {
      const embed = this.querySelector('shopify-forms-embed#app-embed');
      const root = embed?.shadowRoot || this;
      const textarea = root.querySelector('textarea');

      if (!textarea) return;

      if (!this.isMessageCharacterCountEnabled) {
        textarea.removeAttribute('maxlength');
        const existingCounter = textarea.parentElement?.querySelector('.shopify-form-message-char-count');
        if (existingCounter) existingCounter.remove();
        return;
      }

      const maxChars = this.messageMaxChars;
      if (!maxChars) return;

      if (textarea.maxLength !== maxChars) {
        textarea.maxLength = maxChars;
      }

      if (textarea.dataset.shopifyFormCharCountBound !== 'true') {
        textarea.dataset.shopifyFormCharCountBound = 'true';
        textarea.addEventListener('input', () => this.renderMessageCharacterCount(textarea, maxChars));
      }

      this.renderMessageCharacterCount(textarea, maxChars);
    }

    renderMessageCharacterCount(textarea, maxChars) {
      const existingCounter = textarea.parentElement?.querySelector('.shopify-form-message-char-count');
      const counter = existingCounter || textarea.ownerDocument.createElement('p');
      const nextCountText = `${textarea.value.length}/${maxChars}`;

      counter.className = 'shopify-form-message-char-count';
      if (counter.textContent !== nextCountText) {
        counter.textContent = nextCountText;
      }

      if (!existingCounter && textarea.parentElement) {
        textarea.parentElement.appendChild(counter);
      }
    }

    parseMessageMaxChars() {
      if (!this.isMessageCharacterCountEnabled) return null;

      const maxCharsRaw = this.dataset.messageCharacterCountText;
      const maxChars = Number.parseInt(maxCharsRaw, 10);

      if (!Number.isFinite(maxChars) || maxChars <= 0) return null;
      return maxChars;
    }
  }

  customElements.define('shopify-form-wrapper', ShopifyFormWrapper);
}
